import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Autocomplete,
  Chip,
  Button,
  CircularProgress,
  Stack,
  Alert,
  Typography,
  useTheme,
} from '@mui/material';

import {
  isValidEmail,
  dedupeEmails,
  MIN_INTERVAL,
} from '../../../utils/twitterValidation';

const SKELETON = {
  interval_minutes: '',
  active: false,
  recipients: [],
  error_recipients: [],
};

function toForm(config) {
  if (!config) return SKELETON;
  return {
    interval_minutes:
      config.interval_minutes == null ? '' : String(config.interval_minutes),
    active: Boolean(config.active),
    recipients: Array.isArray(config.recipients) ? config.recipients : [],
    error_recipients: Array.isArray(config.error_recipients)
      ? config.error_recipients
      : [],
  };
}

function arraysEqualCI(a, b) {
  if (a.length !== b.length) return false;
  const aSet = new Set(a.map((x) => x.toLowerCase()));
  return b.every((x) => aSet.has(x.toLowerCase()));
}

export default function TwitterConfigDialog({
  open,
  onClose,
  config,
  loading,
  saving,
  onSave,
}) {
  const theme = useTheme();
  const [form, setForm] = useState(SKELETON);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(toForm(config));
      setError(null);
    }
  }, [open, config]);

  const original = useMemo(() => toForm(config), [config]);

  const dedupedRecipients = useMemo(() => dedupeEmails(form.recipients), [form.recipients]);
  const dedupedErrorRecipients = useMemo(
    () => dedupeEmails(form.error_recipients),
    [form.error_recipients],
  );

  const fieldErrors = useMemo(() => {
    const e = {};
    if (form.interval_minutes !== '') {
      const n = Number(form.interval_minutes);
      if (!Number.isInteger(n) || n < MIN_INTERVAL)
        e.interval_minutes = `Integer ≥ ${MIN_INTERVAL}.`;
    }
    const bad = form.recipients.filter((r) => !isValidEmail(r));
    if (bad.length) e.recipients = `Invalid: ${bad.join(', ')}`;
    const badErr = form.error_recipients.filter((r) => !isValidEmail(r));
    if (badErr.length) e.error_recipients = `Invalid: ${badErr.join(', ')}`;
    return e;
  }, [form]);

  const dirty = useMemo(() => {
    if (form.active !== original.active) return true;
    if (form.interval_minutes !== original.interval_minutes) return true;
    if (!arraysEqualCI(dedupedRecipients, original.recipients)) return true;
    if (!arraysEqualCI(dedupedErrorRecipients, original.error_recipients)) return true;
    return false;
  }, [form, original, dedupedRecipients, dedupedErrorRecipients]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const greenSx = {
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: theme.palette.success.main,
    '&:hover': { backgroundColor: theme.palette.success.dark },
  };

  const handleSave = async () => {
    if (hasErrors || !dirty) return;
    setError(null);
    const patch = {};
    if (form.active !== original.active) patch.active = form.active;
    if (form.interval_minutes !== original.interval_minutes) {
      patch.interval_minutes =
        form.interval_minutes === '' ? null : Number(form.interval_minutes);
    }
    if (!arraysEqualCI(dedupedRecipients, original.recipients))
      patch.recipients = dedupedRecipients;
    if (!arraysEqualCI(dedupedErrorRecipients, original.error_recipients))
      patch.error_recipients = dedupedErrorRecipients;

    try {
      await onSave(patch);
      onClose();
    } catch (err) {
      setError(err?.message || String(err));
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <span>Edit Twitter Scraper Settings</span>
        <FormControlLabel
          control={
            <Switch
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              disabled={loading}
              color="success"
            />
          }
          label={form.active ? 'Active' : 'Paused'}
          sx={{ mr: 0 }}
        />
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Interval (minutes)"
            type="number"
            size="small"
            value={form.interval_minutes}
            onChange={(e) => setForm((f) => ({ ...f, interval_minutes: e.target.value }))}
            error={Boolean(fieldErrors.interval_minutes)}
            helperText={fieldErrors.interval_minutes || `Minimum ${MIN_INTERVAL} minutes.`}
            inputProps={{ min: MIN_INTERVAL, step: 1 }}
            sx={{ maxWidth: 240 }}
            disabled={loading}
          />

          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={[]}
            value={form.recipients}
            onChange={(_, next) => setForm((f) => ({ ...f, recipients: next }))}
            renderTags={(tags, getTagProps) =>
              tags.map((opt, idx) => (
                <Chip
                  {...getTagProps({ index: idx })}
                  key={`${opt}-${idx}`}
                  label={opt}
                  size="small"
                  color={isValidEmail(opt) ? 'default' : 'error'}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Recipients"
                placeholder="email@example.com"
                error={Boolean(fieldErrors.recipients)}
                helperText={fieldErrors.recipients || 'Press Enter to add.'}
              />
            )}
            disabled={loading}
          />

          <Autocomplete
            multiple
            freeSolo
            size="small"
            options={[]}
            value={form.error_recipients}
            onChange={(_, next) => setForm((f) => ({ ...f, error_recipients: next }))}
            renderTags={(tags, getTagProps) =>
              tags.map((opt, idx) => (
                <Chip
                  {...getTagProps({ index: idx })}
                  key={`${opt}-${idx}`}
                  label={opt}
                  size="small"
                  color={isValidEmail(opt) ? 'default' : 'error'}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Error recipients"
                placeholder="email@example.com"
                error={Boolean(fieldErrors.error_recipients)}
                helperText={fieldErrors.error_recipients || 'Press Enter to add.'}
              />
            )}
            disabled={loading}
          />

          <Box>
            <Typography variant="caption" color="text.secondary">
              Config changes propagate to the orchestrator within ~60s.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!dirty || hasErrors || saving || loading}
          sx={greenSx}
        >
          {saving ? <CircularProgress size={20} /> : 'Save settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
